import type {
  CrewSeasonDeactivateMode,
  Id,
  Scene,
  SceneCrewPerson,
  SceneCrewRole,
} from "@supermanus/document-model";
import {
  createId,
  isDefaultCrewJobTitle,
} from "@supermanus/document-model";
import type { InputHTMLAttributes, ReactNode } from "react";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DocumentHead } from "./DocumentHead";
import { handleFieldEnterKey } from "./fieldKeyboard";
import { sceneHeaderBg } from "./SceneHeaderColorMenu";

const EPISODE_CARD_ID = "episode";
const NEW_JOB_TITLE_LABEL = "+ Ny jobtitel";

export interface CrewViewProps {
  scenes: Scene[];
  episodeCrewRoles: SceneCrewRole[];
  readOnly?: boolean;
  productionTitle: string;
  seasonTitle?: string | null;
  episodeTitle: string;
  defaultSceneHeaderBg: string;
  /** Known crew names per job title (lowercased key) for autocomplete. */
  nameSuggestionsByJob: Record<string, string[]>;
  /** Default + production-specific job titles shown as chips. */
  jobTitles: string[];
  onChangeCrewRoles: (sceneId: Id, roles: SceneCrewRole[]) => void;
  onChangeEpisodeCrewRoles: (roles: SceneCrewRole[]) => void;
  /** Remember a finished custom job title (blur). */
  onRememberJobTitle?: (title: string) => void;
  /** Remove a custom job-title chip. */
  onRemoveJobTitle?: (title: string) => void;
  /**
   * Peer-wide (“sæson”) credits: activate/deactivate across episodes.
   */
  seasonCredits?: {
    hasPerson: (
      jobTitle: string,
      person: { name: string; detail?: string },
    ) => boolean;
    onActivatePerson: (
      jobTitle: string,
      person: { name: string; detail?: string },
    ) => void;
    onDeactivatePerson: (
      jobTitle: string,
      person: { name: string; detail?: string },
      mode: CrewSeasonDeactivateMode,
    ) => void;
    onRemoveSeasonPerson: (
      jobTitle: string,
      person: { name: string; detail?: string },
    ) => void;
  };
  /** Raw season-wide roles (for aggregate); episode list is already merged. */
  seasonCrewRoles?: SceneCrewRole[];
  onPrintAggregate: () => void;
}

type AggregateBlock = {
  jobTitle: string;
  people: {
    name: string;
    detail?: string;
    sceneNumbers: string[];
    episodeWide?: boolean;
  }[];
};

function namedCreditCount(roles: SceneCrewRole[]): number {
  let n = 0;
  for (const role of roles) {
    for (const person of role.people) {
      if (person.name.trim()) n += 1;
    }
  }
  return n;
}

function creditCountLabel(count: number): string {
  if (count === 0) return "Ingen credits";
  return count === 1 ? "1 credit" : `${count} credits`;
}

function formatCreditScope(p: {
  sceneNumbers: string[];
  episodeWide?: boolean;
}): string | null {
  const parts: string[] = [];
  if (p.episodeWide) parts.push("hele episoden");
  if (p.sceneNumbers.length > 0) parts.push(`sc. ${p.sceneNumbers.join(", ")}`);
  return parts.length ? parts.join(" · ") : null;
}

function aggregateFromRoles(
  scenes: Scene[],
  episodeRoles: SceneCrewRole[],
  seasonRoles?: SceneCrewRole[],
): AggregateBlock[] {
  const byTitle = new Map<
    string,
    Map<
      string,
      {
        name: string;
        detail?: string;
        sceneNumbers: Set<string>;
        episodeWide: boolean;
      }
    >
  >();

  function addRoles(roles: SceneCrewRole[], sceneNumber: string | number | null) {
    for (const role of roles) {
      const title = role.jobTitle.trim();
      if (!title) continue;
      let peopleMap = byTitle.get(title);
      if (!peopleMap) {
        peopleMap = new Map();
        byTitle.set(title, peopleMap);
      }
      for (const person of role.people) {
        const name = person.name.trim();
        if (!name) continue;
        const key = `${name.toLowerCase()}|${(person.detail ?? "").toLowerCase()}`;
        let entry = peopleMap.get(key);
        if (!entry) {
          entry = {
            name,
            detail: person.detail?.trim() || undefined,
            sceneNumbers: new Set(),
            episodeWide: false,
          };
          peopleMap.set(key, entry);
        }
        if (sceneNumber == null) entry.episodeWide = true;
        else entry.sceneNumbers.add(String(sceneNumber));
      }
    }
  }

  if (seasonRoles?.length) addRoles(seasonRoles, null);
  addRoles(episodeRoles, null);
  for (const scene of scenes) addRoles(scene.crewRoles ?? [], scene.number);

  return [...byTitle.keys()]
    .sort((a, b) => a.localeCompare(b, "da", { sensitivity: "base" }))
    .map((jobTitle) => ({
      jobTitle,
      people: [...(byTitle.get(jobTitle)?.values() ?? [])]
        .map((p) => ({
          name: p.name,
          detail: p.detail,
          sceneNumbers: [...p.sceneNumbers].sort((a, b) =>
            a.localeCompare(b, undefined, { numeric: true }),
          ),
          episodeWide: p.episodeWide || undefined,
        }))
        .sort((a, b) =>
          a.name.localeCompare(b.name, "da", { sensitivity: "base" }),
        ),
    }))
    .filter((block) => block.people.length > 0);
}

function filterSuggestions(
  query: string,
  pool: string[],
  exclude: string[],
): string[] {
  const q = query.trim().toLowerCase();
  const excluded = new Set(exclude.map((n) => n.toLowerCase()));
  return pool
    .filter((n) => {
      if (excluded.has(n.toLowerCase())) return false;
      if (!q) return true;
      return n.toLowerCase().includes(q);
    })
    .slice(0, 8);
}

export function CrewView({
  scenes,
  episodeCrewRoles,
  readOnly,
  productionTitle,
  seasonTitle,
  episodeTitle,
  defaultSceneHeaderBg,
  nameSuggestionsByJob,
  jobTitles,
  onChangeCrewRoles,
  onChangeEpisodeCrewRoles,
  onRememberJobTitle,
  onRemoveJobTitle,
  seasonCredits,
  seasonCrewRoles,
  onPrintAggregate,
}: CrewViewProps) {
  const [showAggregate, setShowAggregate] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const foldAnchorRef = useRef<{ el: HTMLElement; top: number } | null>(null);

  useLayoutEffect(() => {
    const anchor = foldAnchorRef.current;
    if (!anchor) return;
    foldAnchorRef.current = null;
    const next = anchor.el.getBoundingClientRect().top;
    const delta = next - anchor.top;
    if (Math.abs(delta) < 1) return;
    const scroller = document.scrollingElement;
    if (scroller) scroller.scrollTop += delta;
    else window.scrollBy(0, delta);
  }, [expanded]);

  const aggregate = useMemo(
    () =>
      showAggregate
        ? aggregateFromRoles(scenes, episodeCrewRoles, seasonCrewRoles)
        : [],
    [showAggregate, scenes, episodeCrewRoles, seasonCrewRoles],
  );

  function toggleExpanded(id: string) {
    const el = document.querySelector<HTMLElement>(
      `[data-crew-card="${CSS.escape(id)}"]`,
    );
    if (el) {
      foldAnchorRef.current = { el, top: el.getBoundingClientRect().top };
    } else {
      foldAnchorRef.current = null;
    }
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="sm-paper sm-paper-a4 sm-view-crew">
      <DocumentHead
        productionTitle={productionTitle}
        seasonTitle={seasonTitle}
        episodeTitle={episodeTitle}
        view="crew"
      />

      <div className="sm-crew-toolbar">
        <button
          type="button"
          className={showAggregate ? "is-active" : undefined}
          onClick={() => setShowAggregate((v) => !v)}
        >
          {showAggregate ? "Vis scener" : "Vis samlet creditliste"}
        </button>
        <button type="button" onClick={onPrintAggregate}>
          Udskriv samlet creditliste
        </button>
      </div>

      {showAggregate ? (
        <section className="sm-crew-aggregate" aria-label="Samlet creditliste">
          {aggregate.length === 0 ? (
            <p className="sm-crew-empty">
              Ingen credits endnu. Tilføj ophavsmænd på episoden eller under de
              enkelte scener.
            </p>
          ) : (
            aggregate.map((block) => (
              <div key={block.jobTitle} className="sm-crew-aggregate-block">
                <h3>{block.jobTitle}</h3>
                <ul>
                  {block.people.map((p) => {
                    const scope = formatCreditScope(p);
                    return (
                      <li key={`${p.name}|${p.detail ?? ""}`}>
                        <strong>{p.name}</strong>
                        {p.detail ? <span> — {p.detail}</span> : null}
                        {scope ? (
                          <span className="sm-crew-scenes"> ({scope})</span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </section>
      ) : (
        <section className="sm-status-matrix" aria-label="Credits">
          <CrewCard
            cardId={EPISODE_CARD_ID}
            title="Hele episoden"
            summary={creditCountLabel(namedCreditCount(episodeCrewRoles))}
            open={expanded.has(EPISODE_CARD_ID)}
            onToggle={() => toggleExpanded(EPISODE_CARD_ID)}
          >
            <p className="sm-status-hint">
              Her kan du tilføje credits på episodeniveau. Credits tilføjet under
              de enkelte scener vises automatisk på den samlede creditliste.
              {seasonCredits
                ? " Brug knappen «Sæson» ud for et navn for at tilføje personen til alle episoder (hele sæsonen)."
                : null}
            </p>
            <CrewRolesEditor
              scopeId="episode"
              roles={episodeCrewRoles}
              readOnly={readOnly}
              jobTitles={jobTitles}
              nameSuggestionsByJob={nameSuggestionsByJob}
              onChange={onChangeEpisodeCrewRoles}
              onRememberJobTitle={onRememberJobTitle}
              onRemoveJobTitle={onRemoveJobTitle}
              seasonCredits={seasonCredits}
            />
          </CrewCard>

          {scenes.map((scene) => {
            const roles = scene.crewRoles ?? [];
            const heading =
              scene.heading?.trim() || scene.title || "Unavngiven";
            const open = expanded.has(scene.id);
            return (
              <CrewCard
                key={scene.id}
                cardId={scene.id}
                title={`Scene ${scene.number}: ${heading}`}
                summary={creditCountLabel(namedCreditCount(roles))}
                headerBg={sceneHeaderBg(
                  scene.headerBackground,
                  defaultSceneHeaderBg,
                )}
                open={open}
                onToggle={() => {
                  toggleExpanded(scene.id);
                }}
              >
                <CrewRolesEditor
                  scopeId={scene.id}
                  roles={roles}
                  readOnly={readOnly}
                  jobTitles={jobTitles}
                  nameSuggestionsByJob={nameSuggestionsByJob}
                  onChange={(next) => onChangeCrewRoles(scene.id, next)}
                  onRememberJobTitle={onRememberJobTitle}
                  onRemoveJobTitle={onRemoveJobTitle}
                />
              </CrewCard>
            );
          })}
        </section>
      )}
    </div>
  );
}

function CrewCard({
  cardId,
  title,
  summary,
  headerBg,
  open,
  onToggle,
  children,
}: {
  cardId: string;
  title: string;
  summary: string;
  headerBg?: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <article
      className={`sm-status-scene${open ? " is-open" : ""}`}
      data-crew-card={cardId}
      style={headerBg ? { backgroundColor: headerBg } : undefined}
      onClick={onToggle}
    >
      <div className="sm-status-scene-row">
        <button
          type="button"
          className="sm-status-expand"
          aria-expanded={open}
          aria-label={open ? "Skjul credits" : "Vis credits"}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          {open ? "▾" : "▸"}
        </button>
        <div className="sm-status-scene-head">
          <span>{title}</span>
          <span className="sm-status-pct">{summary}</span>
        </div>
      </div>
      {open ? (
        <div
          className="sm-status-scene-body sm-crew-card-body"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      ) : null}
    </article>
  );
}

function CrewRolesEditor({
  scopeId,
  roles,
  readOnly,
  jobTitles,
  nameSuggestionsByJob,
  onChange,
  onRememberJobTitle,
  onRemoveJobTitle,
  seasonCredits,
}: {
  scopeId: string;
  roles: SceneCrewRole[];
  readOnly?: boolean;
  jobTitles: string[];
  nameSuggestionsByJob: Record<string, string[]>;
  onChange: (roles: SceneCrewRole[]) => void;
  onRememberJobTitle?: (title: string) => void;
  onRemoveJobTitle?: (title: string) => void;
  seasonCredits?: CrewViewProps["seasonCredits"];
}) {
  const rolesRef = useRef(roles);
  rolesRef.current = roles;
  const rootRef = useRef<HTMLDivElement>(null);
  const pendingEmptyFocusRef = useRef(false);
  const listId = `sm-crew-job-titles-${scopeId}`;
  const [deactivateAsk, setDeactivateAsk] = useState<{
    jobTitle: string;
    person: { name: string; detail?: string };
  } | null>(null);

  useEffect(() => {
    if (!pendingEmptyFocusRef.current) return;
    pendingEmptyFocusRef.current = false;
    const inputs = rootRef.current?.querySelectorAll<HTMLInputElement>(
      ".sm-crew-job",
    );
    const last = inputs?.[inputs.length - 1];
    last?.focus();
  }, [roles]);

  function updateRoles(next: SceneCrewRole[]) {
    if (readOnly) return;
    onChange(next);
  }

  function addRole(jobTitle: string) {
    const current = rolesRef.current;
    if (!jobTitle.trim()) pendingEmptyFocusRef.current = true;
    updateRoles([
      ...current,
      {
        id: createId(),
        jobTitle,
        people: [{ id: createId(), name: "" }],
        sortOrder: current.length,
      },
    ]);
  }

  function setRoleTitle(roleId: Id, jobTitle: string) {
    updateRoles(
      rolesRef.current.map((r) => (r.id === roleId ? { ...r, jobTitle } : r)),
    );
  }

  function addPerson(roleId: Id) {
    updateRoles(
      rolesRef.current.map((r) =>
        r.id === roleId
          ? {
              ...r,
              people: [...r.people, { id: createId(), name: "" }],
            }
          : r,
      ),
    );
  }

  function setPerson(
    roleId: Id,
    personId: Id,
    patch: Partial<Pick<SceneCrewPerson, "name" | "detail">>,
  ) {
    updateRoles(
      rolesRef.current.map((r) =>
        r.id !== roleId
          ? r
          : {
              ...r,
              people: r.people.map((p) =>
                p.id === personId ? { ...p, ...patch } : p,
              ),
            },
      ),
    );
  }

  function removePerson(
    roleId: Id,
    person: SceneCrewPerson,
    jobTitle: string,
  ) {
    const named = person.name.trim();
    if (
      seasonCredits &&
      named &&
      seasonCredits.hasPerson(jobTitle, person)
    ) {
      seasonCredits.onRemoveSeasonPerson(jobTitle, {
        name: named,
        detail: person.detail,
      });
      return;
    }
    updateRoles(
      rolesRef.current
        .map((r) =>
          r.id !== roleId
            ? r
            : { ...r, people: r.people.filter((p) => p.id !== person.id) },
        )
        .filter((r) => r.people.length > 0 || r.id === roleId),
    );
  }

  function removeRole(roleId: Id) {
    updateRoles(rolesRef.current.filter((r) => r.id !== roleId));
  }

  function requestSeasonToggle(
    jobTitle: string,
    person: { name: string; detail?: string },
    nextActive: boolean,
  ) {
    if (!seasonCredits) return;
    if (nextActive) {
      seasonCredits.onActivatePerson(jobTitle, person);
      return;
    }
    setDeactivateAsk({ jobTitle, person });
  }

  return (
    <div className="sm-crew-editor" ref={rootRef}>
      {roles.length === 0 ? (
        <p className="sm-crew-empty">
          Ingen credits endnu. Vælg en jobtitel nedenfor.
        </p>
      ) : null}

      {roles.map((role) => (
        <div key={role.id} className="sm-crew-role">
          <div className="sm-crew-role-head">
            <DraftInput
              className="sm-crew-job"
              list={listId}
              value={role.jobTitle}
              disabled={readOnly}
              onValueChange={(jobTitle) => setRoleTitle(role.id, jobTitle)}
              onBlur={(e) => {
                const title = e.currentTarget.value.trim();
                if (title) onRememberJobTitle?.(title);
              }}
              onKeyDown={handleFieldEnterKey}
              placeholder="Jobtitel"
            />
            {!readOnly ? (
              <button
                type="button"
                className="sm-crew-remove"
                onClick={() => removeRole(role.id)}
              >
                Fjern titel
              </button>
            ) : null}
          </div>
          {role.people.map((person) => {
            const named = person.name.trim();
            const jobTitle = role.jobTitle.trim();
            const onSeason =
              Boolean(seasonCredits) &&
              named.length > 0 &&
              jobTitle.length > 0 &&
              seasonCredits!.hasPerson(role.jobTitle, person);
            return (
              <PersonRow
                key={person.id}
                person={person}
                readOnly={readOnly}
                suggestions={filterSuggestions(
                  person.name,
                  nameSuggestionsByJob[role.jobTitle.trim().toLowerCase()] ??
                    [],
                  role.people
                    .filter((p) => p.id !== person.id)
                    .map((p) => p.name),
                )}
                showDetail={
                  /musik|komponist|tekstforfatter/i.test(role.jobTitle)
                }
                seasonToggle={
                  seasonCredits && !readOnly
                    ? {
                        enabled: named.length > 0 && jobTitle.length > 0,
                        active: onSeason,
                        onToggle: (nextActive: boolean) => {
                          if (!named || !jobTitle) return;
                          requestSeasonToggle(
                            role.jobTitle,
                            { name: named, detail: person.detail },
                            nextActive,
                          );
                        },
                      }
                    : undefined
                }
                onChange={(patch) => setPerson(role.id, person.id, patch)}
                onRemove={() => removePerson(role.id, person, role.jobTitle)}
              />
            );
          })}
          {!readOnly ? (
            <button
              type="button"
              className="sm-crew-add-person"
              onClick={() => addPerson(role.id)}
            >
              + Person
            </button>
          ) : null}
        </div>
      ))}

      {!readOnly ? (
        <div className="sm-crew-add-titles">
          <button
            type="button"
            className="sm-crew-add-title-label"
            onClick={() => addRole("")}
          >
            {NEW_JOB_TITLE_LABEL}
          </button>
          <div className="sm-crew-title-chips">
            {jobTitles
              .filter((title) => title !== NEW_JOB_TITLE_LABEL)
              .map((title) => {
                const removable =
                  Boolean(onRemoveJobTitle) &&
                  !readOnly &&
                  !isDefaultCrewJobTitle(title);
                return (
                  <span key={title} className="sm-crew-title-chip">
                    <button
                      type="button"
                      className="sm-crew-title-chip-add"
                      onClick={() => addRole(title)}
                    >
                      {title}
                    </button>
                    {removable ? (
                      <button
                        type="button"
                        className="sm-crew-title-chip-remove"
                        title={`Fjern «${title}» fra listen`}
                        aria-label={`Fjern jobtitlen ${title} fra listen`}
                        onClick={() => onRemoveJobTitle?.(title)}
                      >
                        ×
                      </button>
                    ) : null}
                  </span>
                );
              })}
            <button
              type="button"
              className="sm-crew-new-title"
              onClick={() => addRole("")}
            >
              {NEW_JOB_TITLE_LABEL}
            </button>
          </div>
          <datalist id={listId}>
            {jobTitles
              .filter((t) => t !== NEW_JOB_TITLE_LABEL)
              .map((t) => (
                <option key={t} value={t} />
              ))}
          </datalist>
        </div>
      ) : null}

      {deactivateAsk && seasonCredits ? (
        <SeasonDeactivateDialog
          personName={deactivateAsk.person.name}
          onCancel={() => setDeactivateAsk(null)}
          onConfirm={(mode) => {
            seasonCredits.onDeactivatePerson(
              deactivateAsk.jobTitle,
              deactivateAsk.person,
              mode,
            );
            setDeactivateAsk(null);
          }}
        />
      ) : null}
    </div>
  );
}

function DraftInput({
  value,
  onValueChange,
  onFocus,
  onBlur,
  ...rest
}: {
  value: string;
  onValueChange: (value: string) => void;
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "defaultValue"
>) {
  const ref = useRef<HTMLInputElement>(null);
  const focusedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || focusedRef.current) return;
    if (el.value !== value) el.value = value;
  }, [value]);

  return (
    <input
      {...rest}
      ref={ref}
      defaultValue={value}
      onFocus={(e) => {
        focusedRef.current = true;
        onFocus?.(e);
      }}
      onChange={(e) => onValueChange(e.target.value)}
      onBlur={(e) => {
        focusedRef.current = false;
        onValueChange(e.target.value);
        onBlur?.(e);
      }}
    />
  );
}

function PersonRow({
  person,
  readOnly,
  suggestions,
  showDetail,
  seasonToggle,
  onChange,
  onRemove,
}: {
  person: SceneCrewPerson;
  readOnly?: boolean;
  suggestions: string[];
  showDetail: boolean;
  seasonToggle?: {
    enabled: boolean;
    active: boolean;
    onToggle: (nextActive: boolean) => void;
  };
  onChange: (patch: Partial<Pick<SceneCrewPerson, "name" | "detail">>) => void;
  onRemove: () => void;
}) {
  const listId = `sm-crew-suggest-${person.id}`;
  /** Instant feedback while the document update catches up. */
  const [optimisticActive, setOptimisticActive] = useState<boolean | null>(
    null,
  );
  const active =
    optimisticActive !== null ? optimisticActive : Boolean(seasonToggle?.active);

  useEffect(() => {
    if (optimisticActive === null || !seasonToggle) return;
    if (seasonToggle.active === optimisticActive) {
      setOptimisticActive(null);
    }
  }, [seasonToggle?.active, optimisticActive, seasonToggle]);

  return (
    <div className="sm-crew-person">
      <DraftInput
        value={person.name}
        disabled={readOnly}
        list={listId}
        placeholder="Navn"
        onValueChange={(name) => onChange({ name })}
        onKeyDown={handleFieldEnterKey}
      />
      <datalist id={listId}>
        {suggestions.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
      {showDetail ? (
        <DraftInput
          value={person.detail ?? ""}
          disabled={readOnly}
          placeholder="Detalje (fx sangtitel)"
          onValueChange={(detail) => onChange({ detail })}
          onKeyDown={handleFieldEnterKey}
        />
      ) : null}
      {seasonToggle ? (
        <button
          type="button"
          className={`sm-crew-season-btn${active ? " is-active" : ""}`}
          disabled={!seasonToggle.enabled}
          aria-pressed={active}
          title={
            !seasonToggle.enabled
              ? person.name.trim()
                ? "Angiv en jobtitel først"
                : "Skriv et navn først"
              : active
                ? "Slå sæson-credit fra"
                : "Tilføj til alle episoder i sæsonen"
          }
          onClick={() => {
            if (!seasonToggle.enabled) return;
            const next = !active;
            // Only paint green immediately when activating; deactivation waits
            // for the confirm dialog.
            if (next) setOptimisticActive(true);
            seasonToggle.onToggle(next);
          }}
        >
          Sæson
        </button>
      ) : null}
      {!readOnly ? (
        <button type="button" className="sm-crew-remove" onClick={onRemove}>
          ×
        </button>
      ) : null}
    </div>
  );
}

function SeasonDeactivateDialog({
  personName,
  onConfirm,
  onCancel,
}: {
  personName: string;
  onConfirm: (mode: CrewSeasonDeactivateMode) => void;
  onCancel: () => void;
}) {
  const [mode, setMode] = useState<CrewSeasonDeactivateMode>("all");

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="sm-dialog-backdrop" role="presentation" onClick={onCancel}>
      <div
        className="sm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sm-crew-season-off-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="sm-crew-season-off-title">Fjern sæson-credit</h2>
        <p>
          «{personName}» er tilføjet hele sæsonen. Hvor skal personen fjernes?
        </p>
        <ul className="sm-dialog-choice-list">
          <li>
            <label className={mode === "all" ? "is-selected" : undefined}>
              <input
                type="radio"
                name="sm-crew-season-off"
                checked={mode === "all"}
                onChange={() => setMode("all")}
              />
              <span>
                Fra alle episoder i sæsonen
                <span className="sm-dialog-choice-preview">
                  Fjernes overalt, også her
                </span>
              </span>
            </label>
          </li>
          <li>
            <label className={mode === "others" ? "is-selected" : undefined}>
              <input
                type="radio"
                name="sm-crew-season-off"
                checked={mode === "others"}
                onChange={() => setMode("others")}
              />
              <span>
                Fra alle øvrige episoder
                <span className="sm-dialog-choice-preview">
                  Bliver stående på denne episode
                </span>
              </span>
            </label>
          </li>
          <li>
            <label className={mode === "this" ? "is-selected" : undefined}>
              <input
                type="radio"
                name="sm-crew-season-off"
                checked={mode === "this"}
                onChange={() => setMode("this")}
              />
              <span>
                Kun fra denne episode
                <span className="sm-dialog-choice-preview">
                  Bliver stående på de øvrige episoder
                </span>
              </span>
            </label>
          </li>
        </ul>
        <div className="sm-dialog-actions">
          <button type="button" onClick={onCancel}>
            Annuller
          </button>
          <button
            type="button"
            className="sm-dialog-primary"
            onClick={() => onConfirm(mode)}
          >
            Fjern
          </button>
        </div>
      </div>
    </div>
  );
}
